import { shouldDeliverCableMessage } from '../channelMessageDeduper.js';

const subs = new Map();

const normalizePayload = (payload) => {
  if (typeof payload !== 'string') return payload;
  try {
    return JSON.parse(payload);
  } catch {
    return payload;
  }
};

const unwrapActionCableEnvelope = (payload) => {
  if (!payload || typeof payload !== 'object') return payload;
  if (!payload.identifier || payload.message == null) return payload;
  return normalizePayload(payload.message);
};

const releaseConversationEventsSubscription = (userId) => {
  const key = String(userId);
  const existing = subs.get(key);
  if (existing?.subscription?.unsubscribe) {
    try {
      existing.subscription.unsubscribe();
    } catch {}
  }
  subs.delete(key);
};

const sendConversationTyping = (userId, conversationId, typing, activity = null) => {
  const key = String(userId);
  const subscription = subs.get(key)?.subscription;
  if (!subscription?.sendTyping) {
    return false;
  }
  try {
    subscription.sendTyping(conversationId, typing, activity);
    return true;
  } catch {
    return false;
  }
};

const conversationEventsChannel = (cable, userId, handlers = {}) => {
  if (!cable || typeof cable?.subscriptions?.create !== 'function') return null;
  if (!userId) return null;

  const key = String(userId);
  const nextHandlers = {
    onConnected: handlers.onConnected || null,
    onDisconnected: handlers.onDisconnected || null,
    onReceive: handlers.onReceive || null,
  };

  const existing = subs.get(key);
  if (existing) {
    if (existing.cable !== cable) {
      releaseConversationEventsSubscription(key);
    } else {
      existing.handlers.onConnected = nextHandlers.onConnected;
      existing.handlers.onDisconnected = nextHandlers.onDisconnected;
      existing.handlers.onReceive = nextHandlers.onReceive;
      return existing.subscription;
    }
  }

  try {
    const handlerRef = { ...nextHandlers };
    const subscription = cable.subscriptions.create(
      { channel: 'ConversationEventsChannel' },
      {
        connected: () => handlerRef.onConnected?.(),
        disconnected: () => handlerRef.onDisconnected?.(),
        received: (payload) => {
          const data = unwrapActionCableEnvelope(normalizePayload(payload));
          if (!shouldDeliverCableMessage(`conversation-events:${key}`, data)) return;
          handlerRef.onReceive?.(data);
        },
        sendTyping(conversationId, typing, activity = null) {
          try {
            this.perform('typing', {
              conversation_id: conversationId,
              typing,
              activity,
            });
          } catch {}
        },
        sendCallSignal(payload = {}) {
          try {
            this.perform('call_signal', {
              conversation_id: payload.conversationId ?? payload.conversation_id,
              call_id: payload.callId ?? payload.call_id,
              signal_type: payload.signalType ?? payload.signal_type,
              from_client_id: payload.fromClientId ?? payload.from_client_id,
              payload: payload.payload || {},
            });
          } catch {}
        },
      },
    );
    subs.set(key, { subscription, handlers: handlerRef, cable });
    return subscription;
  } catch (error) {
    console.warn('Conversation events channel subscription failed', error);
    return null;
  }
};

export { releaseConversationEventsSubscription, sendConversationTyping };
export default conversationEventsChannel;
